// Libraries
import _ from 'lodash';
import React, {forwardRef, useImperativeHandle} from 'react';

// Supermove
import {gql, DocumentNode} from '@supermove/graphql';
import {useState} from '@supermove/hooks';
import {PayengineCreditCardModel, ProjectModel} from '@supermove/models';

// App
import DocumentV2ClientCreditCardList from '@shared/modules/Document/components/DocumentV2ClientCreditCardList';
import PayengineCreditCardForm from '@shared/modules/Payment/forms/PayengineCreditCardForm';
import useSavePayEngineCreditCardV3 from '@shared/modules/Payment/hooks/useSavePayEngineCreditCardV3';

export type DocumentV2SaveClientCardHandle = {
  submit: () => Promise<void>;
};

type DocumentV2SaveClientCreditCardContentProps = {
  project: ProjectModel;
  initialSelectedCreditCardId: string | undefined;
  allowSelectExistingPaymentMethods: boolean;
  onSelect: (payengineCreditCard: PayengineCreditCardModel | undefined) => void;
};

const DocumentV2SaveClientCreditCardContent = forwardRef<
  DocumentV2SaveClientCardHandle,
  DocumentV2SaveClientCreditCardContentProps
>(({project, initialSelectedCreditCardId, onSelect, allowSelectExistingPaymentMethods}, ref) => {
  const [selectedCreditCardId, setSelectedCreditCardId] = useState<string | 'new' | undefined>(
    initialSelectedCreditCardId,
  );

  const handleSelect = (creditCard: PayengineCreditCardModel | undefined) => {
    setSelectedCreditCardId(creditCard?.id ?? 'new');
    onSelect(creditCard);
  };

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const payengineCreditCardForm = PayengineCreditCardForm.new({
    isDefault: _.isEmpty(project.client.creditCards),
    projectId: _.toNumber(project.id),
  });
  const {form, handleSubmit, setCreditCardClient} = useSavePayEngineCreditCardV3({
    clientId: _.toNumber(project.client.id),
    payengineCreditCardForm,
    onSuccess: () => {
      setErrorMessage(null);
    },
    onError: (error) => {
      console.error(`Error executing SavePayengineCreditCardMutation: `, error);
      setErrorMessage(`Could not save credit card.`);
    },
    handleCardErrorMessage: setErrorMessage,
  });

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!selectedCreditCardId) {
        return Promise.resolve();
      } else if (selectedCreditCardId === 'new') {
        try {
          const response = await handleSubmit();
          if (response?.id) {
            // TODO: (zharkyn) We don't have entire credit card object here, passing only id
            onSelect({
              id: _.toString(response.id),
              cardSummaryName: response.cardSummaryName,
            } as PayengineCreditCardModel);
            return Promise.resolve();
          }
        } catch (error) {
          console.error(`Error saving credit card:`, error);
          return Promise.reject(error);
        }
      }
    },
  }));

  const field = `form.payengineCreditCardForm`;

  return (
    <React.Fragment>
      <DocumentV2ClientCreditCardList
        errorTransactionMessage={errorMessage}
        errorTokenizeMessage={null}
        client={project.client}
        form={form}
        field={field}
        selectedCreditCardId={selectedCreditCardId}
        allowSelectExistingPaymentMethods={allowSelectExistingPaymentMethods}
        onSelect={handleSelect}
        setCreditCardClient={setCreditCardClient}
      />
    </React.Fragment>
  );
});

type DocumentV2SaveClientCreditCardProps = {
  project: ProjectModel;
  allowSelectExistingPaymentMethods: boolean;
  initialSelectedCreditCardId: string | undefined;
  onSelect: (payengineCreditCard: PayengineCreditCardModel | undefined) => void;
};

type DocumentV2SaveClientCreditCardComponent = React.ForwardRefExoticComponent<
  DocumentV2SaveClientCreditCardProps & React.RefAttributes<DocumentV2SaveClientCardHandle>
> & {
  fragment: DocumentNode;
};

const DocumentV2SaveClientCreditCard = forwardRef<
  DocumentV2SaveClientCardHandle,
  DocumentV2SaveClientCreditCardProps
>(({project, allowSelectExistingPaymentMethods, initialSelectedCreditCardId, onSelect}, ref) => {
  return (
    <DocumentV2SaveClientCreditCardContent
      ref={ref}
      project={project}
      initialSelectedCreditCardId={initialSelectedCreditCardId}
      allowSelectExistingPaymentMethods={allowSelectExistingPaymentMethods}
      onSelect={(payengineCreditCard) => {
        onSelect(payengineCreditCard);
      }}
    />
  );
}) as DocumentV2SaveClientCreditCardComponent;

// --------------------------------------------------
// Data
// --------------------------------------------------
DocumentV2SaveClientCreditCard.fragment = gql`
  ${DocumentV2ClientCreditCardList.fragment}

  fragment DocumentV2SaveClientCreditCard_Project on Project {
    id
    client {
      id
      creditCards {
        id
        cardName
        cardSummaryName
        isDefault
        expireDate
      }
      ...DocumentV2ClientCreditCardList
    }
  }

  fragment DocumentV2SaveClientCreditCard_Document on Document {
    ${gql.query}
    job {
      id
      project {
        id
        ...DocumentV2SaveClientCreditCard_Project
      }
    }
    invoice {
      id
      project {
        id
        ...DocumentV2SaveClientCreditCard_Project
      }
    }
  }
`;

export default DocumentV2SaveClientCreditCard;
